package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.IdentityData
import com.neurobs.ccabserver.administrator.contexts.{GuiContext, UserContext}
import com.neurobs.ccabserver.administrator.view.{AdminView, SubjectSelectionView}
import com.neurobs.js.ccab.{CCABServer, RelativeJSCCABServer}
import com.neurobs.js.controller.{AdminPanelController, AdminPanelDelegate}
import com.neurobs.js.dom.ElementFactory
import com.neurobs.js.view.{Container, Gui}
import com.neurobs.react.HtmlAdapter
import com.neurobs.util.{Guid, PrintLogger}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react

import scala.util.Success
import scala.concurrent.ExecutionContext.Implicits.global

class AdminController(user: IdentityData, primaryEmail: String, gui: Gui, dataPassword: DataPasswordCache) {
	private val recordCache = new RecordCache
	private implicit val ccabServer = RelativeJSCCABServer()


	private val view: AdminView = {
		val view = new AdminView(user.isSiteAdmin)
		if (user.isSiteAdmin) {
			view.addLink(title = "Accounts", () => addPanel(AccountListController(gui, user, dataPassword, recordCache, addPanel(_))))
			view.addLink("Releases", { () =>
				CCABServer.getReleaseTypes {
					case Right(types) =>
						for (t <- types) addPanel(ReleaseListController(gui, t))
					case _ =>
				}
			})
			view.addLink(title = "Tests", () => addPanel(AdminTestListController(gui)))
			view.addLink(title = "Study Types", () => addPanel(StudyTypeListController(gui, user, addPanel(_))))
			view.addLink(title = "Examiner Tags", () => addPanel(AdminExaminerTagListController(gui, ccabServer)))
			view.addLink(title = "Errors", () => addPanel(ErrorListController(gui, recordCache)))
			view.addLink(title = "Error Classes", () => addPanel(ErrorClassListController(gui, recordCache)))
			view.addLink(title = "Error Types", () => addPanel(ErrorTypeListController(gui, recordCache)))
			view.addLink(title = "Keys", () => addPanel(KeyListController()))
			view.addLink(title = "Test Failures", () => addPanel(new TestRunErrorController(recordCache)))
			view.addLink(title = "Misc", () => addPanel(new AdminMiscController(gui, user)))
			view.addLink(title = "Chart Test", () => addPanel(new ChartTestController(gui)))
			view.addLink(title = "SLog Test", () => {
				ccabServer.getExaminerTags()._2 map {
					case Right(tags) => addPanel(new SessionLogController(gui, new PrintLogger(printDebug = false), Guid("ef557163-8e8f-48ca-a38a-5c87c99a1aba").get, sessionId = 2309, tags = new ExaminerTagList(tags)))
					case Left(_) =>
				}
			})
			view.addLink(title = "Test Transcripts", () => addPanel(TestTranscriptListController(gui)))
			view.addLink(title = "Event Server Events", () => addPanel(EventServerEventListController(gui)))

			//view.addLink(title = "Transcription Admin", () => addPanel(new DbTranscriptionAdminController(gui, user)))
			view.addLink(title = "Transcript Review Admin", () => addPanel(new TranscriptReviewAdminController(gui, user)))
			view.addLink(title = "Translation Admin", () => addPanel(new TranslationAdminController(gui, user)))
			//view.addLink(title = "Event Server Admin", () => addPanel(new EventServerAdminController(gui)))
			view.newLinkRow()
		}
		view.addLink(title = "Studies", () => addPanel(StudyListController(gui, user.legacyAccountId, primaryEmail, dataPassword, user, addPanel(_))))
			//view.addLink(title = "Sessions", () => addPanel(TestSessionListController(gui, user.accountId, primaryEmail)))

		view.addLink(title = "Translation Tool", () => addPanel(new TranslationToolController(gui, user)))
		view.addLink("Clear", { () => view.clearPanels() })
		view
	}

	def container: Container = view.container

	private def addPanel(panel: AdminPanelController, after: Option[view.PanelId] = None): Unit = {
		val newId = view.addPanel(panel.container, after)
		panel.didAppear()
		panel.delegate = Some(new AdminPanelDelegate {
			override def newPanel(panel: AdminPanelController): Unit = addPanel(panel, Some(newId))
			override def close(): Unit = view.removePanel(newId)
		})
	}
}

@react object AdminControllerAdapter {
	type Props = Unit

	val component = FunctionalComponent[Props] { props =>
		val user = UserContext.accessState()
		val gui = GuiContext.accessState()

		HtmlAdapter(element = ElementFactory.toElement(new AdminController(user = user.user.get, primaryEmail = "email", gui = gui, dataPassword = user.value.dataPasswords).container))
	}
}